import { Link } from 'react-router-dom';
import MotionWrapper from '../../../components/base/MotionWrapper';

const PricingSection = () => {
  const pricingTiers = [
    {
      name: 'Foundation',
      price: '$2,500',
      description: 'Core automation setup for smaller teams',
      features: [
        'Basic workflow mapping & documentation',
        'Email automation sequences',
        'Simple task routing',
        'Basic reporting dashboard',
        '2-week implementation'
      ],
      badge: null,
      ring: false
    },
    {
      name: 'Growth',
      price: '$5,500',
      description: 'Comprehensive automation for scaling organizations',
      features: [
        'Advanced workflow architecture',
        'Multi-channel automation (email, SMS, calls)',
        'CRM integration & customization',
        'Advanced analytics & insights',
        'Team training & documentation',
        '4-week implementation'
      ],
      badge: 'Most Popular',
      ring: true
    },
    {
      name: 'Professional',
      price: '$9,500',
      description: 'Enterprise-grade systems for complex operations',
      features: [
        'Custom workflow development',
        'Advanced integrations & APIs',
        'Multi-department coordination',
        'Custom reporting & dashboards',
        'Ongoing optimization support',
        '6-week implementation'
      ],
      badge: null,
      ring: false
    },
    {
      name: 'Enterprise Automation Architecture',
      price: '$15,000 - $30,000+',
      priceNote: '(custom scoped)',
      description: 'Designed for larger organizations or specialized operational environments',
      features: [
        'Multi-platform workflow orchestration',
        'Cross-department automation systems',
        'Custom AI-supported process layers',
        'Governance, documentation, and internal training included'
      ],
      customNote: 'Price determined based on system complexity and data environment. Begins with a Workflow Audit to map automation readiness.',
      badge: 'Enterprise',
      ring: false
    }
  ];

  return (
    <section 
      id="pricing" 
      className="py-20 lg:py-28"
      style={{ backgroundColor: '#F8F4EF' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <MotionWrapper className="text-center mb-16">
          <h2 
            className="text-4xl lg:text-5xl font-bold mb-6"
            style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
          >
            Automation Investment Levels
          </h2>
          <p 
            className="text-xl lg:text-2xl leading-relaxed max-w-4xl mx-auto"
            style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
          >
            Choose the automation package that fits your organization's scale and complexity needs.
          </p>
        </MotionWrapper>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 lg:gap-12">
          {pricingTiers.map((tier, index) => (
            <MotionWrapper
              key={tier.name}
              delay={index * 80}
              className={`motion-hover-card relative bg-white rounded-2xl p-8 lg:p-10 border-2 transition-all duration-200 ${
                tier.ring ? 'ring-2 ring-offset-4' : ''
              }`}
              style={{ 
                borderColor: tier.ring ? '#7B6655' : '#E6DACD',
                ringColor: tier.ring ? '#7B6655' : undefined,
                ringOffsetColor: '#F8F4EF'
              }}
            >
              {tier.badge && (
                <div 
                  className={`absolute -top-4 left-8 px-4 py-2 rounded-full text-sm font-semibold text-white ${
                    tier.badge === 'Enterprise' ? 'bg-gray-800' : ''
                  }`}
                  style={{ 
                    backgroundColor: tier.badge === 'Enterprise' ? '#2B2B2B' : '#7B6655',
                    fontFamily: 'Manrope, Inter, sans-serif'
                  }}
                >
                  {tier.badge}
                </div>
              )}
              
              <div className="mb-8">
                <h3 
                  className="text-2xl lg:text-3xl font-bold mb-2"
                  style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  {tier.name}
                </h3>
                <div className="mb-4">
                  <span 
                    className="text-3xl lg:text-4xl font-bold"
                    style={{ color: '#7B6655', fontFamily: 'Manrope, Inter, sans-serif' }}
                  >
                    {tier.price}
                  </span>
                  {tier.priceNote && (
                    <span 
                      className="text-lg ml-2"
                      style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    >
                      {tier.priceNote}
                    </span>
                  )}
                </div>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                >
                  {tier.description}
                </p>
              </div>

              <div className="mb-8">
                <ul className="space-y-4">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <i 
                        className="ri-check-line text-xl mr-3 mt-0.5 flex-shrink-0"
                        style={{ color: '#7B6655' }}
                      ></i>
                      <span 
                        className="text-base leading-relaxed"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {tier.customNote && (
                <div className="mb-8 p-4 rounded-lg" style={{ backgroundColor: '#F8F4EF' }}>
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                  >
                    {tier.customNote}
                  </p>
                </div>
              )}

              <Link
                to={`/get-started?package=${encodeURIComponent(tier.name === 'Enterprise Automation Architecture' ? 'Enterprise' : tier.name)}`}
                className="motion-hover-button w-full block text-center px-8 py-4 rounded-lg font-semibold text-white whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#7B6655', fontFamily: 'Manrope, Inter, sans-serif' }}
              >
                Get Started
              </Link>
            </MotionWrapper>
          ))}
        </div>

        <MotionWrapper delay={320} className="text-center mt-16">
          <p 
            className="text-lg leading-relaxed max-w-3xl mx-auto"
            style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
          >
            All packages include a comprehensive workflow audit, system documentation, team training, and 30 days of post-implementation support.
          </p>
        </MotionWrapper>
      </div>
    </section>
  );
};

export default PricingSection;
